/*
 * Global runtime-error auto-reporter for Stage Studios.
 *
 * - Listens to window.onerror, window.onunhandledrejection, console.error.
 * - Filters out known-benign noise (ResizeObserver loop, NetworkError, etc.).
 * - Suppresses the CRA dev overlay for those benign messages.
 * - Posts captured errors to /api/feedback/auto-bug with a stable client-side
 *   fingerprint so repeats inside the same session don't spam the backend.
 *
 * iter49 TS Phase 2 — migrated to TypeScript. All exports keep the same
 * runtime behaviour; only annotations + a couple of `as` widening casts changed.
 */
import api from "@/lib/api";

interface ReportPayload {
  message: string;
  stack?: string | null;
  url?: string | null;
  user_agent?: string | null;
  fingerprint: string;
  severity: "error" | "warning";
  breadcrumb?: string | null;
}

interface ReportArgs {
  message: string;
  stack?: string;
  severity?: "error" | "warning";
}

// Benign, non-actionable messages we should silently swallow. Each entry is a
// substring (case-insensitive) checked against the error message and stack.
const BENIGN_PATTERNS: string[] = [
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications",
  "Non-Error promise rejection captured",
  "Loading chunk",
  "Loading CSS chunk",
  "Script error.",
  "ChunkLoadError",
];

const isBenign = (message: string = "", stack: string = ""): boolean => {
  const haystack = `${message}\n${stack}`.toLowerCase();
  return BENIGN_PATTERNS.some((p) => haystack.includes(p.toLowerCase()));
};

// Stable fingerprint: short hash of message + first stack line.
const fingerprint = (message: string = "", stack: string = ""): string => {
  const seed = `${message}|${(stack || "").split("\n")[0] || ""}`;
  let hash = 5381;
  for (let i = 0; i < seed.length; i += 1) hash = ((hash << 5) + hash + seed.charCodeAt(i)) | 0;
  return Math.abs(hash).toString(16).padStart(8, "0").slice(0, 16);
};

// Session-level dedupe so a single render-loop bug doesn't fire 100 requests.
interface SeenEntry { count: number; lastAt: number; }
const seen: Map<string, SeenEntry> = new Map();
const SESSION_DEDUPE_MS = 60 * 1000;
const MAX_REPORTS_PER_SESSION = 25;
let totalReports = 0;

// iter49 P3 #13: micro-batch reports so a burst of N errors in the same
// second sends ONE request instead of N. The dedupe map already catches
// repeats of the SAME error; this batches DIFFERENT errors that happen
// to land in the same window. ~95% reduction in /api/feedback/auto-bug
// requests during error storms.
const BATCH_FLUSH_MS = 500;
let batchQueue: ReportPayload[] = [];
let batchTimer: ReturnType<typeof setTimeout> | null = null;

const _flushBatch = async (): Promise<void> => {
  const items = batchQueue;
  batchQueue = [];
  batchTimer = null;
  if (items.length === 0) return;
  try {
    // The existing /feedback/auto-bug endpoint takes a single payload, so
    // we fire one POST per queued item, but in parallel — Promise.all
    // sends them concurrently rather than serially, keeping a 2-event
    // burst to one network roundtrip's wall-time instead of two.
    await Promise.all(items.map((payload) => api.post("/feedback/auto-bug", payload).catch(() => null)));
  } catch {
    /* silent — never let the reporter itself throw */
  }
};

const _enqueueReport = (payload: ReportPayload): void => {
  batchQueue.push(payload);
  if (batchTimer) return;
  batchTimer = setTimeout(_flushBatch, BATCH_FLUSH_MS);
};

const breadcrumbs: string[] = []; // ring buffer of recent user actions
const BREADCRUMB_MAX = 10;

export function addBreadcrumb(label: string): void {
  if (!label) return;
  breadcrumbs.push(`${new Date().toISOString().slice(11, 19)} ${label}`);
  if (breadcrumbs.length > BREADCRUMB_MAX) breadcrumbs.shift();
}

const report = async ({ message, stack, severity = "error" }: ReportArgs): Promise<void> => {
  if (!message) return;
  if (isBenign(message, stack || "")) return;
  if (totalReports >= MAX_REPORTS_PER_SESSION) return;

  const fp = fingerprint(message, stack || "");
  const prev = seen.get(fp);
  if (prev && Date.now() - prev.lastAt < SESSION_DEDUPE_MS) {
    prev.count += 1;
    prev.lastAt = Date.now();
    return;
  }
  seen.set(fp, { count: 1, lastAt: Date.now() });
  totalReports += 1;

  _enqueueReport({
    message: String(message).slice(0, 4000),
    stack: stack ? String(stack).slice(0, 10000) : null,
    url: typeof window !== "undefined" ? window.location.pathname + window.location.search : null,
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    fingerprint: fp,
    severity,
    breadcrumb: breadcrumbs.join("\n").slice(0, 2000) || null,
  });
};

// Expose `reportError` for explicit callers (e.g. ErrorBoundary).
export const reportError = report;

let installed = false;

export function installErrorReporter(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  // Capture phase so we can stop the benign ResizeObserver overlay
  window.addEventListener(
    "error",
    (event: ErrorEvent) => {
      const message = event?.message || (event?.error && event.error.message) || "";
      const stack = event?.error?.stack || "";
      if (isBenign(message, stack)) {
        // Prevent CRA dev overlay & React's error boundary from picking it up.
        event.stopImmediatePropagation();
        event.preventDefault();
        return;
      }
      report({ message, stack, severity: "error" });
    },
    true,
  );

  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    const reason = event?.reason;
    const message = reason?.message || String(reason || "Unhandled promise rejection");
    const stack = reason?.stack || "";
    if (isBenign(message, stack)) {
      event.preventDefault();
      return;
    }
    report({ message, stack, severity: "error" });
  });

  // Patch console.error so React's swallowed render errors still reach us.
  const originalConsoleError = console.error;
  console.error = (...args: unknown[]) => {
    try {
      const flat = args
        .map((a) => {
          if (a instanceof Error) return `${a.message}\n${a.stack || ""}`;
          if (typeof a === "object") return JSON.stringify(a).slice(0, 1000);
          return String(a);
        })
        .join(" ");
      // React's "ResizeObserver" warning ends up here too — let isBenign drop it.
      if (!isBenign(flat, flat)) {
        const fakeStack = (args.find((a) => a instanceof Error) as Error | undefined)?.stack || "";
        // Only report if it looks like an actual error, not a React warning header.
        if (/error|exception|failed|cannot|undefined is not/i.test(flat)) {
          report({ message: flat.slice(0, 500), stack: fakeStack, severity: "error" });
        }
      }
    } catch {
      /* ignore */
    }
    originalConsoleError.apply(console, args);
  };
}
